import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { ICreditLinesFilters } from '../models/credit-lines-filters.model';
import { ICreditLineStatus, ICreditLines } from '../models/financing-request.model';
import { Injectable } from '@angular/core';
import { FinancingApiService } from '../api/financing-api.service';
import { GetMyPublicProfileAction, GetRequestStatusOptionsAction, SearchCreditLinesListAction, SearchCreditOffersListAction, SearchCreditRequestsListAction } from '../actions/financing.actions';
import { catchError, tap } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { Constants } from '../util/constants';
import {PageableApiResult} from "../models/pagable-api-result.model";
import { ICreditOffersFilters } from '../models/offers-filters.model';
import { ICreditRequestFilters } from '../models/credit-request-filters.model';
import { IPublicProfile } from '../models/public-profile.model';
import { OffersApiService } from '../api/offers-api.service';

export interface FinancingStateModel {
  publicProfile: IPublicProfile;
  creditLinesList: ICreditLines[];
  creditLinesPageableList: PageableApiResult<ICreditLines>;
  creditLinesFilters: ICreditLinesFilters;
  offersList: ICreditLines[];
  offersPageableList: PageableApiResult<ICreditLines>;
  offersFilters: ICreditOffersFilters;
  requestsList: ICreditLines[];
  requestsPageableList: PageableApiResult<ICreditLines>;
  requestsFilters: ICreditRequestFilters;
  requestsStatusOptions: ICreditLineStatus[];
}

const initialState: FinancingStateModel = {
  publicProfile: null,
  creditLinesList: null,
  creditLinesPageableList: null,
  creditLinesFilters: null,
  offersList: null,
  offersPageableList: null,
  offersFilters: null,
  requestsList: null,
  requestsPageableList: null,
  requestsFilters: null,
  requestsStatusOptions: null
};

const FINANCIG_STATE_TOKEN = new StateToken<FinancingStateModel>('financing');

@State<FinancingStateModel>({
  name: FINANCIG_STATE_TOKEN,
  defaults: initialState,
})
@Injectable()
export class FinancingState {
  constructor(private financingApiService: FinancingApiService, private offersApi: OffersApiService) {}

  @Action(GetMyPublicProfileAction)
  handleGetMyPublicProfileAction(ctx: StateContext<FinancingStateModel>, action: GetMyPublicProfileAction) {
    return this.financingApiService.getPublicProfile().pipe(
      tap((perfil) => {
        console.log('perifl', perfil)
        if (perfil.actividad && perfil.presentacion && perfil.documentacion?.length != 0) {
          ctx.patchState({
            publicProfile: perfil
          });
        }
      }),
      catchError((err) => throwError(err))
    );
  }

  @Selector()
  static getPublicProfile(state: FinancingStateModel) {
    return state?.publicProfile;
  }

  @Action(SearchCreditLinesListAction)
  handleSearchCreditLinesListAction(ctx: StateContext<FinancingStateModel>, action: SearchCreditLinesListAction) {
    const filter = ctx.getState()?.creditLinesFilters;
    const actionFilter = action.payload.filter;

    let reqFilter: ICreditLinesFilters;
    if (filter || actionFilter) {
      reqFilter = {
        ...filter,
        ...actionFilter,
      };
    } else {
      reqFilter = {
        page: 0,
        size: Constants.PAGE_SIZE_EXCHANGE,
        razonSocial: null,
        montoMin: null,
        montoMax: null,
        score: null,
        idNumber: null,
        facturacionMin: null,
        facturacionMax: null,
        daGarantia: null,
        favoritos: null,
        sortByMonto: 'ASC',
      };
    }

    ctx.patchState({
      creditLinesFilters: reqFilter,
    });

    return this.financingApiService.getCreditLinesList(reqFilter).pipe(
      tap((lineas) => {
        const state = ctx.getState();
        ctx?.patchState({
          creditLinesList: lineas.content,
          creditLinesPageableList: lineas,
        });
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  @Selector()
  static getCreditLinesList(state: FinancingStateModel) {
    return state?.creditLinesPageableList;
  }

  @Action(SearchCreditOffersListAction)
  handleSearchCreditOffersListAction(ctx: StateContext<FinancingStateModel>, action: SearchCreditOffersListAction) {
    const filter = ctx.getState()?.offersFilters;
    const actionFilter = action.payload.filter;

    let reqFilter: ICreditOffersFilters;
    if (filter || actionFilter) {
      reqFilter = {
        ...filter,
        ...actionFilter,
      };
    } else {
      reqFilter = {
        page: 0,
        size: 10,
        razonSocial: null,
        montoMin: null,
        montoMax: null,
        score: null,
        motivo: null,
        estado: null,
        intencionMin: null,
        intencionMax: null,
        daGarantia: null,
        fechaMin: null,
        fechaMax: null,
        sortByFecha: 'DESC'
      };
    }

    ctx.patchState({
      offersFilters: reqFilter,
    });

    if (reqFilter.isInvestor) {
      return this.offersApi.getCreatedInvestorOffers(reqFilter).pipe(
      tap((offers) => {
        ctx.patchState({
          offersList: offers.content,
          offersPageableList: offers,
        });
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
    } else {
      return this.offersApi.getRecievedOffers(reqFilter).pipe(
      tap((offers) => {
        ctx.patchState({
          offersList: offers.content,
          offersPageableList: offers,
        });
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
    }
  }

  @Selector()
  static getCreditOffersList(state: FinancingStateModel) {
    return state?.offersPageableList;
  }

  

  @Action(SearchCreditRequestsListAction)
  handleSearchCreditRequestListAction(ctx: StateContext<FinancingStateModel>, action: SearchCreditRequestsListAction) {
    const filter = ctx.getState()?.requestsFilters;
    const actionFilter = action.payload.filter;

    let reqFilter: ICreditRequestFilters;
    if (filter || actionFilter) {
      reqFilter = {
        ...filter,
        ...actionFilter,
      };
    } else {
      reqFilter = {
        page: 0,
        size: 10,
        montoMin: null,
        montoMax: null,
        motivo: null,
        estado: null,
        daGarantia: null,
        fechaMin: null,
        fechaMax: null,
        sortByFecha: 'DESC'
      };
    }

    ctx.patchState({
      requestsFilters: reqFilter,
    });

    return this.financingApiService.getSelfCreditLinesRequests(reqFilter).pipe(
      tap((requests) => {
        ctx.patchState({
          requestsList: requests.content,
          requestsPageableList: requests,
        });
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  @Selector()
  static getCreditRequestList(state: FinancingStateModel) {
    return state?.requestsPageableList;
  }

  @Action(GetRequestStatusOptionsAction)
  handleGetRequestStatusOptionsAction(ctx: StateContext<FinancingStateModel>, action: GetRequestStatusOptionsAction) {
    return this.financingApiService.getCreditRequestStatusOptions().pipe(
      tap((stats) => {
        ctx.patchState({
          requestsStatusOptions: stats
        });
      }),
      catchError((err) => throwError(err))
    );
  }

  @Selector()
  static getCreditRequestStatusOptions(state: FinancingStateModel) {
    return state?.requestsStatusOptions;
  }
}
